import * as XLSX from 'xlsx-js-style';
import { BrandStore } from '../services/brandSitesService';
import { calculateStoreRecommendations } from './recommendationLogic';

export function exportBrandRecommendationsExcel(brandName: string, stores: BrandStore[]): void {
  const data: any[] = [];

  // Add header rows
  data.push(['Brand Recommendations Export']);
  data.push([`Brand Name: ${brandName}`]);
  data.push([`Export Date: ${new Date().toISOString().split('T')[0]}`]);
  data.push([`Total Stores: ${stores.length}`]);
  data.push([]);

  // Add column headers
  data.push([
    'Store Name', 'Address', 'City', 'State', 'Zip Code',
    'Purchek Recommended', 'Purchek Stores (Neighbourhood)', 'Purchek Market Penetration', 'Crime Incidents', 'Purchek Reasons',
    'Containment Recommended', 'Containment Stores (Neighbourhood)', 'Containment Market Penetration', 'Crime Incidents', 'Containment Reasons',
    'Cart Manager Recommended', 'Cold Days (Annual)', 'Hot Days (Annual)', 'Cart Manager Reasons'
  ]);

  // Add store data
  stores.forEach((store) => {
    const recommendations = calculateStoreRecommendations(store);
    const nearby = store.nearby_penetration || {};
    const geo = store.geo || {};

    const formatPercent = (val: any) => {
      if (!val) return '-';
      const num = parseFloat(val);
      return isNaN(num) ? '-' : `${num.toFixed(2)}%`;
    };

    const storeNum = store.updated_store_ids?.[0]?.store_id || '';
    const displayName = storeNum ? `${store.brand_name || store.site || ''} ${storeNum}` : store.site || '';
    data.push([
      displayName,
      store.address || '-',
      geo.city || '-',
      geo.state || '-',
      store.zipcode ? String(store.zipcode) : '-',
      recommendations.purchek.length > 0 ? 'Yes' : '-',
      nearby.purchek_customer_count ?? '-',
      formatPercent(nearby.penetration_purchek),
      geo.total_crime_incidents ?? '-',
      recommendations.purchek.length > 0 ? recommendations.purchek.join('; ') : '',
      recommendations.containment.length > 0 ? 'Yes' : '-',
      nearby.containment_customer_count ?? '-',
      formatPercent(nearby.penetration_containment),
      geo.total_crime_incidents ?? '-',
      recommendations.containment.length > 0 ? recommendations.containment.join('; ') : '',
      recommendations.cartManager.length > 0 ? 'Yes' : '-',
      geo.no_of_cold_days ?? '-',
      geo.no_of_hot_days ?? '-',
      recommendations.cartManager.length > 0 ? recommendations.cartManager.join('; ') : ''
    ]);
  });

  const ws = XLSX.utils.aoa_to_sheet(data);

  // Set column widths
  ws['!cols'] = [
    { wch: 25 }, { wch: 35 }, { wch: 15 }, { wch: 12 }, { wch: 12 },   // Store Name, Address, City, State, Zip Code
    { wch: 22 }, { wch: 32 }, { wch: 28 }, { wch: 18 }, { wch: 40 },   // Purchek Recommended, Purchek Stores (Neighbourhood), Purchek Market Penetration, Crime Incidents, Purchek Reasons
    { wch: 26 }, { wch: 36 }, { wch: 32 }, { wch: 18 }, { wch: 40 },   // Containment Recommended, Containment Stores (Neighbourhood), Containment Market Penetration, Crime Incidents, Containment Reasons
    { wch: 28 }, { wch: 20 }, { wch: 20 }, { wch: 40 }                  // Cart Manager Recommended, Cold Days (Annual), Hot Days (Annual), Cart Manager Reasons
  ];

  // Apply styles to header row (row 6, index 5)
  const headerRow = 5;
  const numCols = 19;
  for (let col = 0; col < numCols; col++) {
    const cellRef = XLSX.utils.encode_cell({ r: headerRow, c: col });
    if (!ws[cellRef]) ws[cellRef] = { t: 's', v: '' };
    ws[cellRef].s = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "4472C4" } },
      alignment: { horizontal: "center", vertical: "center" }
    };
  }

  // Apply alternating row colors to data rows
  for (let row = headerRow + 1; row < data.length; row++) {
    const isEven = (row - headerRow) % 2 === 0;
    const fillColor = isEven ? "E8E8E8" : "FFFFFF";
    
    for (let col = 0; col < numCols; col++) {
      const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
      if (!ws[cellRef]) ws[cellRef] = { t: 's', v: '' };
      ws[cellRef].s = {
        fill: { fgColor: { rgb: fillColor } },
        alignment: { vertical: "center" }
      };
    }
  }

  // Add autofilter on header row so columns are sortable/filterable in Excel
  ws['!autofilter'] = {
    ref: `${XLSX.utils.encode_cell({ r: headerRow, c: 0 })}:${XLSX.utils.encode_cell({ r: data.length - 1, c: numCols - 1 })}`
  };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Recommendations');

  const timestamp = new Date().toISOString().slice(0, 10);
  const filename = `${brandName.replace(/\s+/g, '_')}_recommendations_${timestamp}.xlsx`;

  XLSX.writeFile(wb, filename);
}

/**
 * Export nearby stores from the map view to Excel with 2 sheets:
 * 1. Neighbourhood Overview - summary stats and recommendations
 * 2. Nearby Stores - detailed store list
 */
export interface RecommendationConfigs {
  purchekConfig?: Record<string, string>;
  cartManagerConfig?: Record<string, string>;
  containmentConfig?: Record<string, string>;
  subscriptionConfig?: Record<string, string>;
  pairedWheelConfig?: Record<string, string>;
  purchekReadinessName?: string;
  cartManagerReadinessName?: string;
  containmentReadinessName?: string;
  subscriptionReadinessName?: string;
  pairedWheelReadinessName?: string;
}

export function exportStoreMapData(referenceBrand: string, stores: any[], referenceStore?: any, radius?: number, configs?: RecommendationConfigs): void {
  if (!stores || stores.length === 0) {
    console.warn('No stores data available for export');
    return;
  }

  const wb = XLSX.utils.book_new();
  const headerStyle = {
    font: { bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: "4472C4" } },
    alignment: { horizontal: "center" as const, vertical: "center" as const }
  };
  const labelStyle = {
    font: { bold: true },
    fill: { fgColor: { rgb: "D6E4F0" } },
    alignment: { vertical: "center" as const }
  };
  const valueStyle = {
    alignment: { vertical: "center" as const }
  };

  // Helper to get status
  const getStatus = (store: any) => {
    const hasAnySolution = !!(store.purchek_install_date || store.containment_install_date || store.cart_manager_install_date || store.subscription_start_date || store.paired_wheel_install_date);
    return (store.customer_id || store.is_customer || hasAnySolution) ? 'Customer' : 'Non-Customer';
  };

  const getCrimeLabel = (crimeRate: number | null | undefined) => {
    if (crimeRate == null) return 'No Data';
    if (crimeRate >= 5000) return 'Critical';
    if (crimeRate >= 1000) return 'High';
    if (crimeRate >= 200) return 'Medium';
    if (crimeRate > 0) return 'Low';
    return 'No Data';
  };

  // ==========================================
  // Sheet 1: Neighbourhood Overview
  // ==========================================
  const overviewData: any[] = [];

  // Title
  overviewData.push(['Neighbourhood Overview']);
  overviewData.push([`Export Date: ${new Date().toISOString().split('T')[0]}`]);
  overviewData.push([]);

  // Reference Store Details
  overviewData.push(['Reference Store Details']);
  overviewData.push(['Store Name', referenceStore?.site || referenceBrand]);
  overviewData.push(['Brand', referenceStore?.brand_name || referenceBrand]);
  overviewData.push(['Address', [referenceStore?.address, referenceStore?.city, referenceStore?.state, referenceStore?.zipcode].filter(Boolean).join(', ') || '-']);
  overviewData.push(['Status', referenceStore ? getStatus(referenceStore) : '-']);
  overviewData.push(['Installed Solutions', referenceStore ? [
    referenceStore.purchek_install_date ? 'Purchek' : '',
    referenceStore.containment_install_date ? 'Cart Containment' : '',
    referenceStore.cart_manager_install_date ? 'Cart Manager' : '',
    referenceStore.subscription_start_date ? 'Subscription' : '',
    referenceStore.paired_wheel_install_date ? 'Paired Wheel' : ''
  ].filter(Boolean).join(', ') || 'None' : '-']);
  overviewData.push(['Crime Index', referenceStore ? getCrimeLabel(referenceStore.crime_rate) : '-']);
  overviewData.push(['Search Radius', radius ? `${radius} miles` : '-']);
  overviewData.push([]);

  // Summary Counts
  const customerCount = stores.filter(s => getStatus(s) === 'Customer').length;
  const nonCustomerCount = stores.length - customerCount;
  const purchekCount = stores.filter(s => s.purchek_install_date).length;
  const containmentCount = stores.filter(s => s.containment_install_date).length;
  const cartManagerCount = stores.filter(s => s.cart_manager_install_date).length;
  const subscriptionCount = stores.filter(s => s.subscription_start_date).length;
  const pairedWheelCount = stores.filter(s => s.paired_wheel_install_date).length;

  overviewData.push(['Neighbourhood Summary']);
  overviewData.push(['Total Nearby Stores', stores.length]);
  overviewData.push(['Customers', customerCount]);
  overviewData.push(['Non-Customers', nonCustomerCount]);
  overviewData.push(['Total Recovery in Neighbourhood ($)', referenceStore?.shoplifting_amount_usd != null ? Math.round(referenceStore.shoplifting_amount_usd) : '-']);
  overviewData.push(['Pushout Prevented in Neighbourhood', referenceStore?.shoplifting_incident_count != null ? referenceStore.shoplifting_incident_count : '-']);
  overviewData.push([]);

  overviewData.push(['Installed Solutions Breakdown']);
  overviewData.push(['No of Purchek Sites', purchekCount]);
  overviewData.push(['No of Cart Containment Sites', containmentCount]);
  overviewData.push(['No of Cart Manager Sites', cartManagerCount]);
  overviewData.push(['No of Subscription Sites', subscriptionCount]);
  overviewData.push(['No of Paired Wheel Sites', pairedWheelCount]);
  overviewData.push([]);

  // Helper: convert flat store structure to nested format for calculateStoreRecommendations
  const toNestedStore = (store: any) => ({
    ...store,
    geo: {
      total_crime_incidents: store.crime_rate ?? 0,
      city: store.city,
      state: store.state,
      penalty_for_cart_owner: store.penalty_for_cart_owner,
      no_of_hot_days: store.no_of_hot_days ?? 0,
      no_of_cold_days: store.no_of_cold_days ?? 0,
      law_summary: store.law_summary
    },
    nearby_penetration: {
      purchek_customer_count: store.purchek_customer_count ?? 0,
      penetration_purchek: store.penetration_purchek ?? 0,
      containment_customer_count: store.containment_customer_count ?? 0,
      penetration_containment: store.penetration_containment ?? 0,
      penetration_paired_wheel: store.penetration_paired_wheel ?? 0,
      plaza_cm_customer_count: store.plaza_cm_customer_count ?? 0
    }
  });

  // Compute recommendations for the reference store (Yes/No)
  const refStoreForCalc = referenceStore ? toNestedStore(referenceStore) : null;
  const refRecommendations = refStoreForCalc ? calculateStoreRecommendations(
    refStoreForCalc, referenceStore?.estimated_no_of_carts_per_store ?? 0,
    configs?.purchekConfig, configs?.cartManagerConfig, configs?.containmentConfig,
    configs?.subscriptionConfig, configs?.pairedWheelConfig,
    configs?.purchekReadinessName, configs?.cartManagerReadinessName,
    configs?.containmentReadinessName, configs?.subscriptionReadinessName,
    configs?.pairedWheelReadinessName
  ) : null;

  // Helper: build a row with label, Yes/No, then each reason in its own cell
  const buildRecRow = (label: string, reasons: string[] | undefined) => {
    const yesNo = refRecommendations ? ((reasons && reasons.length > 0) ? 'Yes' : 'No') : '-';
    return [label, yesNo, ...(reasons || [])];
  };

  overviewData.push(['Recommendations Summary']);
  overviewData.push(buildRecRow('Purchek Recommended', refRecommendations?.purchek));
  overviewData.push(buildRecRow('Cart Containment Recommended', refRecommendations?.containment));
  overviewData.push(buildRecRow('Cart Manager Recommended', refRecommendations?.cartManager));
  overviewData.push(buildRecRow('Subscription Recommended', refRecommendations?.subscription));
  overviewData.push(buildRecRow('Paired Wheel Recommended', refRecommendations?.pairedWheel));

  // Find max columns needed (label + yes/no + max reasons)
  const maxReasons = Math.max(
    refRecommendations?.purchek.length ?? 0,
    refRecommendations?.containment.length ?? 0,
    refRecommendations?.cartManager.length ?? 0,
    refRecommendations?.subscription.length ?? 0,
    refRecommendations?.pairedWheel.length ?? 0
  );
  const overviewWs = XLSX.utils.aoa_to_sheet(overviewData);
  const colWidths: any[] = [{ wch: 30 }, { wch: 15 }];
  for (let i = 0; i < maxReasons; i++) {
    colWidths.push({ wch: 55 });
  }
  overviewWs['!cols'] = colWidths;

  // Style section headers
  const sectionHeaderRows = [0, 3, 12, 19, 26];
  sectionHeaderRows.forEach(row => {
    const cellRef = XLSX.utils.encode_cell({ r: row, c: 0 });
    if (overviewWs[cellRef]) {
      overviewWs[cellRef].s = headerStyle;
    }
    // Merge across all columns for section headers
    const mergeCols = Math.max(2, 2 + maxReasons) - 1;
    if (!overviewWs['!merges']) overviewWs['!merges'] = [];
    overviewWs['!merges'].push({ s: { r: row, c: 0 }, e: { r: row, c: mergeCols } });
  });

  // Style label/value/reason rows
  overviewData.forEach((row, idx) => {
    if (sectionHeaderRows.includes(idx) || row.length === 0 || row.length < 2) return;
    const labelCell = XLSX.utils.encode_cell({ r: idx, c: 0 });
    const valueCell = XLSX.utils.encode_cell({ r: idx, c: 1 });
    if (overviewWs[labelCell]) overviewWs[labelCell].s = labelStyle;
    if (overviewWs[valueCell]) overviewWs[valueCell].s = valueStyle;
    // Style reason cells (columns 2+)
    for (let c = 2; c < row.length; c++) {
      const cell = XLSX.utils.encode_cell({ r: idx, c });
      if (overviewWs[cell]) overviewWs[cell].s = valueStyle;
    }
  });

  XLSX.utils.book_append_sheet(wb, overviewWs, 'Neighbourhood Overview');

  // ==========================================
  // Sheet 2: Nearby Stores
  // ==========================================
  const storesData: any[] = [];

  storesData.push(['Nearby Stores']);
  storesData.push([`Reference Brand: ${referenceBrand}`]);
  storesData.push([`Export Date: ${new Date().toISOString().split('T')[0]}`]);
  storesData.push([`Total Stores: ${stores.length}`]);
  storesData.push([]);

  // Helper to convert text to title case
  const toTitleCase = (str: string | null | undefined): string => {
    if (!str) return '';
    return str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  };

  storesData.push([
    'Store Name',
    'Address',
    'Brand',
    'Distance (mi)',
    'Status',
    'Customer ID',
    'Installed Solutions',
    'Total Recovery (Neighbourhood)',
    'Total Recovery (Site)',
    'Pushout Prevented (Neighbourhood)',
    'Pushout Prevented (Site)',
    'Notes'
  ]);

  stores.forEach((store) => {
    const status = getStatus(store);

    const installedSolutions = [
      store.purchek_install_date ? 'Purchek' : '',
      store.containment_install_date ? 'Cart Containment' : '',
      store.cart_manager_install_date ? 'Cart Manager' : '',
      store.subscription_start_date ? 'Subscription' : '',
      store.paired_wheel_install_date ? 'Paired Wheel' : ''
    ].filter(Boolean).join(', ') || '-';

    const addressParts = [
      toTitleCase(store.address),
      toTitleCase(store.city),
      toTitleCase(store.state),
      store.zipcode,
      toTitleCase(store.county)
    ].filter(Boolean).join(', ') || '-';

    const shopliftingAmountUsd = store.shoplifting_amount_usd ?? store.nearby_penetration?.shoplifting_amount_usd ?? null;
    const totalCrimeAmountUsd = store.total_crime_amount_usd ?? store.video?.total_crime_amount_usd ?? null;
    const shopliftingIncidentCount = store.shoplifting_incident_count ?? store.nearby_penetration?.shoplifting_incident_count ?? null;
    const videoIncidentCount = store.video_incident_count ?? store.video?.incident_count ?? null;

    const recoveryNeighbourhood = shopliftingAmountUsd != null ? `$${Math.round(shopliftingAmountUsd).toLocaleString()}` : '-';
    const recoverySite = status === 'Non-Customer' ? 'No Solution Installed' : (totalCrimeAmountUsd != null ? `$${Math.round(totalCrimeAmountUsd).toLocaleString()}` : '-');
    const pushoutNeighbourhood = shopliftingIncidentCount != null ? shopliftingIncidentCount.toLocaleString() : '-';
    const pushoutSite = status === 'Non-Customer' ? 'No Solution Installed' : (videoIncidentCount != null ? videoIncidentCount.toLocaleString() : '-');

    const notes = store.store_notes && store.store_notes.length > 0
      ? store.store_notes.map((n: any) => n.comment).join('\n')
      : '';

    storesData.push([
      store.site || '',
      addressParts,
      store.brand_name || '',
      store.distance !== undefined ? store.distance : '-',
      status,
      store.customer_id || '-',
      installedSolutions,
      recoveryNeighbourhood,
      recoverySite,
      pushoutNeighbourhood,
      pushoutSite,
      notes
    ]);
  });

  const storesWs = XLSX.utils.aoa_to_sheet(storesData);
  storesWs['!cols'] = [
    { wch: 25 }, { wch: 45 }, { wch: 20 }, { wch: 15 }, { wch: 16 },   // Store Name, Address, Brand, Distance (mi), Status
    { wch: 20 }, { wch: 40 },                                            // Customer ID, Installed Solutions
    { wch: 30 }, { wch: 22 }, { wch: 34 }, { wch: 24 },                 // Total Recovery (N'hood), Total Recovery (Site), Pushout Prevented (N'hood), Pushout Prevented (Site)
    { wch: 40 }                                                           // Notes
  ];

  // Style stores sheet header row
  const storesHeaderRow = 5;
  const storesNumCols = 12;
  for (let col = 0; col < storesNumCols; col++) {
    const cellRef = XLSX.utils.encode_cell({ r: storesHeaderRow, c: col });
    if (!storesWs[cellRef]) storesWs[cellRef] = { t: 's', v: '' };
    storesWs[cellRef].s = headerStyle;
  }

  // Alternating row colors
  for (let row = storesHeaderRow + 1; row < storesData.length; row++) {
    const isEven = (row - storesHeaderRow) % 2 === 0;
    const fillColor = isEven ? "E8E8E8" : "FFFFFF";
    for (let col = 0; col < storesNumCols; col++) {
      const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
      if (!storesWs[cellRef]) storesWs[cellRef] = { t: 's', v: '' };
      storesWs[cellRef].s = {
        fill: { fgColor: { rgb: fillColor } },
        alignment: { vertical: "center" }
      };
    }
  }

  // Add autofilter on header row so columns are sortable/filterable in Excel
  storesWs['!autofilter'] = {
    ref: `${XLSX.utils.encode_cell({ r: storesHeaderRow, c: 0 })}:${XLSX.utils.encode_cell({ r: storesData.length - 1, c: storesNumCols - 1 })}`
  };

  XLSX.utils.book_append_sheet(wb, storesWs, 'Nearby Stores');

  const timestamp = new Date().toISOString().slice(0, 10);
  const filename = `${referenceBrand.replace(/\s+/g, '_')}_neighbourhood_${timestamp}.xlsx`;

  XLSX.writeFile(wb, filename);
}

export function exportOpportunitiesByRegionData(stores: any[], brandName?: string, sortBy?: string, crmStoreIds?: Set<string>): void {
  if (!stores || stores.length === 0) {
    console.warn('No stores data available for export');
    return;
  }

  const wb = XLSX.utils.book_new();
  const headerStyle = {
    font: { bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: "4472C4" } },
    alignment: { horizontal: "center" as const, vertical: "center" as const }
  };
  const titleStyle = {
    font: { bold: true, size: 14, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: "203864" } },
    alignment: { horizontal: "center" as const, vertical: "center" as const }
  };
  const labelStyle = {
    font: { bold: true },
    fill: { fgColor: { rgb: "D6E4F0" } },
    alignment: { vertical: "center" as const }
  };
  const valueStyle = {
    alignment: { vertical: "center" as const }
  };

  const getCrimeLabel = (crimeRate: number | null | undefined) => {
    if (crimeRate == null) return 'No Data';
    if (crimeRate >= 5000) return 'Critical';
    if (crimeRate >= 1000) return 'High';
    if (crimeRate >= 200) return 'Medium';
    if (crimeRate > 0) return 'Low';
    return 'No Data';
  };

  const page1Data: any[] = [];

  page1Data.push(['Brand Recommendations Export']);
  page1Data.push([`Export Date: ${new Date().toISOString().split('T')[0]}`]);
  page1Data.push([`Total Stores: ${stores.length}`]);
  page1Data.push([]);

  const isCustomer = (s: any): boolean => s.isAcquired || s.customer_id != null || s.status === 'Customer';
  const getRecommendationCount = (s: any): number =>
    (s.recommendations?.purchek?.length > 0 ? 1 : 0) +
    (s.recommendations?.containment?.length > 0 ? 1 : 0) +
    (s.recommendations?.cartManager?.length > 0 ? 1 : 0) +
    (s.recommendations?.subscription?.length > 0 ? 1 : 0) +
    (s.recommendations?.pairedWheel?.length > 0 ? 1 : 0);

  const customerCount = stores.filter(s => isCustomer(s)).length;
  const nonCustomerCount = stores.length - customerCount;
  const getCrimeIncidents = (s: any): number => s.geo?.total_crime_incidents ?? s.crimeIncidents ?? 0;
  const criticalCrimeStoreCount = stores.filter(s => getCrimeIncidents(s) >= 5000).length;
  const highCrimeStoreCount = stores.filter(s => getCrimeIncidents(s) >= 1000 && getCrimeIncidents(s) < 5000).length;
  const mediumCrimeStoreCount = stores.filter(s => getCrimeIncidents(s) >= 200 && getCrimeIncidents(s) < 1000).length;
  const purchekCount = stores.filter(s => s.recommendations?.purchek?.length > 0).length;
  const containmentCount = stores.filter(s => s.recommendations?.containment?.length > 0).length;
  const cartManagerCount = stores.filter(s => s.recommendations?.cartManager?.length > 0).length;
  const pairedWheelCount = stores.filter(s => s.recommendations?.pairedWheel?.length > 0).length;
  const customerOpportunities = stores.filter(s => isCustomer(s)).reduce((sum, s) => sum + getRecommendationCount(s), 0);
  const nonCustomerOpportunities = stores.filter(s => !isCustomer(s)).reduce((sum, s) => sum + getRecommendationCount(s), 0);

  page1Data.push(['Customer Stores :', customerCount]);
  page1Data.push(['Non Customer Stores', nonCustomerCount]);
  page1Data.push(['Opportunities in Critical Crime Area (5000+ crimes/year)', criticalCrimeStoreCount]);
  page1Data.push(['Opportunities in High Crime Area (1000+crimes/year)', highCrimeStoreCount]);
  page1Data.push(['Opportunities in Medium Crime Area (500-1000 crimes/year)', mediumCrimeStoreCount]);
  page1Data.push(['Opportunities with current customers', customerOpportunities]);
  page1Data.push(['Opportunities with non customers', nonCustomerOpportunities]);
  page1Data.push(['Number of Purchek Opportunities', purchekCount]);
  page1Data.push(['Number of Containment Opportunities', containmentCount]);
  page1Data.push(['Number of Cart Manager Opportunities', cartManagerCount]);
  page1Data.push(['Number of Paired Wheel Opportunities', pairedWheelCount]);
  // Find state with highest number of opportunities (stores with recommendations)
  const stateOpportunityCounts: Record<string, number> = {};
  stores.forEach(s => {
    const state = s.state || s.geo?.state || '';
    if (state && getRecommendationCount(s) > 0) {
      const stateName = state.charAt(0).toUpperCase() + state.slice(1).toLowerCase();
      stateOpportunityCounts[stateName] = (stateOpportunityCounts[stateName] || 0) + 1;
    }
  });
  const topState = Object.entries(stateOpportunityCounts).sort((a, b) => b[1] - a[1])[0];
  page1Data.push(['State with Highest Number of Opportunities', topState ? topState[0] : '-']);

  const page1Ws = XLSX.utils.aoa_to_sheet(page1Data);
  page1Ws['!cols'] = [{ wch: 50 }, { wch: 20 }];

  for (let col = 0; col <= 1; col++) {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
    if (page1Ws[cellRef]) {
      page1Ws[cellRef].s = titleStyle;
    }
  }

  if (!page1Ws['!merges']) page1Ws['!merges'] = [];
  page1Ws['!merges'].push({ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } });

  page1Data.forEach((row, idx) => {
    if (idx < 4) return;
    const labelCell = XLSX.utils.encode_cell({ r: idx, c: 0 });
    if (page1Ws[labelCell]) page1Ws[labelCell].s = labelStyle;
    if (row.length > 1) {
      const valueCell = XLSX.utils.encode_cell({ r: idx, c: 1 });
      if (page1Ws[valueCell]) page1Ws[valueCell].s = valueStyle;
    }
  });

  // Right-align the state name value (last row)
  const stateRowIdx = page1Data.length - 1;
  const stateValueCell = XLSX.utils.encode_cell({ r: stateRowIdx, c: 1 });
  if (page1Ws[stateValueCell]) {
    page1Ws[stateValueCell].s = { alignment: { vertical: "center" as const, horizontal: "right" as const } };
  }

  XLSX.utils.book_append_sheet(wb, page1Ws, 'Summary');

  const page2Data: any[] = [];

  page2Data.push(['Store Details']);
  page2Data.push([`Export Date: ${new Date().toISOString().split('T')[0]}`]);
  page2Data.push([`Total Stores: ${stores.length}`]);
  page2Data.push([]);

  // Sort stores by the selected rank field for sequential ranking
  const getRankValue = (store: any, field: string): number => {
    if (field === 'purchekRank') return store.purchekRank ?? store.purchek_rank ?? Infinity;
    if (field === 'containmentRank') return store.containmentRank ?? store.containment_rank ?? Infinity;
    if (field === 'cartManagerRank') return store.cartManagerRank ?? store.cart_manager_rank ?? Infinity;
    return store.rank ?? store.combo_rank ?? Infinity;
  };

  const hasDisplayRank = stores.some(s => s.displayRank != null);

  const sortedStores = [...stores].sort((a, b) => {
    if (hasDisplayRank) {
      const aRank = a.displayRank ?? Infinity;
      const bRank = b.displayRank ?? Infinity;
      return aRank - bRank;
    }
    const aVal = getRankValue(a, sortBy || 'rank');
    const bVal = getRankValue(b, sortBy || 'rank');
    return aVal - bVal;
  });

  const buildRankMap = (storesList: any[], field: string): Map<any, number> => {
    const sorted = [...storesList].sort((a, b) => getRankValue(a, field) - getRankValue(b, field));
    const map = new Map<any, number>();
    sorted.forEach((store, idx) => {
      map.set(store, idx + 1);
    });
    return map;
  };

  const activeRankMap = hasDisplayRank ? null : buildRankMap(stores, sortBy || 'rank');

  page2Data.push([
    'Rank',
    'Store Name',
    'Address',
    'City',
    'State',
    'Zip Code',
    'Crime Index',
    'Crime Incidents/year',
    'Solutions Installed',
    'Customer Since',
    'Last Billed',
    'Status',
    'Purchek Recommended',
    'Purchek Stores (Neighbourhood)',
    'Purchek Market Penetration',
    'Containment Recommended',
    'Containment Stores (Neighbourhood)',
    'Containment Market Penetration',
    'Cart Manager Recommended',
    'Cold Days (Annual)',
    'Hot Days (Annual)',
    'Total Recovery (Neighbourhood)',
    'Total Recovery (Site)',
    'Pushout Prevented (Neighbourhood)',
    'Pushout Prevented (Site)',
    'Lead Status',
    'Notes'
  ]);

  const formatRecCell = (reasons: string[] | undefined) => {
    if (!reasons || reasons.length === 0) return 'No';
    return 'Yes\n' + reasons.map((r, i) => `Reason ${i + 1}: ${r}`).join('\n');
  };

  sortedStores.forEach((store) => {
    // Handle both raw store format and mapped store format
    const storeId = store.storeId || store.updated_store_ids?.[0]?.store_id || '';
    const storeName = storeId ? `${store.brandName || store.brand_name || store.name || store.site || ''} ${storeId}` : store.site || store.name || '';
    const address = store.address || store.location || '-';
    const city = store.city || '-';
    const state = store.state || '-';
    const zipcode = store.zipcode || store.zipCode || '-';
    const crimeIncidents = getCrimeIncidents(store);
    const crimeIndex = getCrimeLabel(crimeIncidents);
    const status = (store.isAcquired || store.customer_id != null || store.status === 'Customer') ? 'Customer' : 'Non-Customer';

    // Build solutions installed list
    const installed: string[] = [];
    const solutionsStr = store.solutions || '';
    if (store.purchek_install_date || solutionsStr.includes('Purchek')) installed.push('Purchek');
    if (store.containment_install_date || solutionsStr.includes('Cart Containment')) installed.push('Cart Containment');
    if (store.cart_manager_install_date || solutionsStr.includes('Cart Manager')) installed.push('Cart Manager');
    if (store.subscription_start_date || solutionsStr.includes('Subscription')) installed.push('Subscription');
    if (store.paired_wheel_install_date || solutionsStr.includes('Paired Wheel')) installed.push('Paired Wheel');
    const solutionsInstalled = installed.length > 0 ? installed.join(', ') : 'None';

    const rec = store.recommendations;

    // Total Recovery & Pushout Prevented (Neighbourhood + Site)
    const shopliftingAmountUsd = store.shopliftingAmountUsd ?? store.nearby_penetration?.shoplifting_amount_usd ?? null;
    const totalCrimeAmountUsd = store.totalCrimeAmountUsd ?? store.video?.total_crime_amount_usd ?? null;
    const shopliftingIncidentCount = store.shopliftingIncidentCount ?? store.nearby_penetration?.shoplifting_incident_count ?? null;
    const videoIncidentCount = store.videoIncidentCount ?? store.video?.incident_count ?? null;

    const recoveryNeighbourhood = shopliftingAmountUsd != null ? `$${Math.round(shopliftingAmountUsd).toLocaleString()}` : '-';
    const recoverySite = status === 'Non-Customer' ? 'No Solution Installed' : (totalCrimeAmountUsd != null ? `$${Math.round(totalCrimeAmountUsd).toLocaleString()}` : '-');
    const pushoutNeighbourhood = shopliftingIncidentCount != null ? shopliftingIncidentCount.toLocaleString() : '-';
    const pushoutSite = status === 'Non-Customer' ? 'No Solution Installed' : (videoIncidentCount != null ? videoIncidentCount.toLocaleString() : '-');

    page2Data.push([
      (hasDisplayRank ? store.displayRank : activeRankMap?.get(store)) ?? '-',
      storeName,
      address,
      city,
      state,
      String(zipcode),
      crimeIndex,
      crimeIncidents || 0,
      solutionsInstalled,
      store.firstPurchaseDate || store.first_purchase || '-',
      store.lastPurchaseDate || store.last_purchase || '-',
      status,
      formatRecCell(rec?.purchek),
      store.purchekCustomerCount ?? store.nearby_penetration?.purchek_customer_count ?? 0,
      store.purchekPenetration != null ? `${parseFloat(String(store.purchekPenetration)).toFixed(2)}%` : (store.nearby_penetration?.penetration_purchek != null ? `${parseFloat(String(store.nearby_penetration.penetration_purchek)).toFixed(2)}%` : '0%'),
      formatRecCell(rec?.containment),
      store.containmentCustomerCount ?? store.nearby_penetration?.containment_customer_count ?? 0,
      store.containmentPenetration != null ? `${parseFloat(String(store.containmentPenetration)).toFixed(2)}%` : (store.nearby_penetration?.penetration_containment != null ? `${parseFloat(String(store.nearby_penetration.penetration_containment)).toFixed(2)}%` : '0%'),
      formatRecCell(rec?.cartManager),
      store.coldDays ?? store.geo?.no_of_cold_days ?? 0,
      store.hotDays ?? store.geo?.no_of_hot_days ?? 0,
      recoveryNeighbourhood,
      recoverySite,
      pushoutNeighbourhood,
      pushoutSite,
      crmStoreIds?.has(store.poiId || store.storeId || '') ? 'Exported to CRM' : 'Not Exported to CRM',
      (store.store_notes && store.store_notes.length > 0)
        ? store.store_notes.map((n: any) => n.comment).join('\n')
        : '-'
    ]);
  });

  const page2Ws = XLSX.utils.aoa_to_sheet(page2Data);
  page2Ws['!cols'] = [
    { wch: 8 },                                                       // Rank
    { wch: 25 }, { wch: 35 }, { wch: 15 }, { wch: 12 }, { wch: 12 }, // Store Name, Address, City, State, Zip Code
    { wch: 15 }, { wch: 22 },                                         // Crime Index, Crime Incidents/year
    { wch: 35 }, { wch: 16 }, { wch: 16 }, { wch: 14 },               // Solutions Installed, Customer Since, Last Billed, Status
    { wch: 40 }, { wch: 32 }, { wch: 28 },                            // Purchek Recommended, Purchek Stores (Neighbourhood), Purchek Market Penetration
    { wch: 40 }, { wch: 36 }, { wch: 32 },                            // Containment Recommended, Containment Stores (Neighbourhood), Containment Market Penetration
    { wch: 40 },                                                       // Cart Manager Recommended
    { wch: 20 }, { wch: 20 },                                         // Cold Days (Annual), Hot Days (Annual)
    { wch: 30 }, { wch: 22 }, { wch: 34 }, { wch: 24 },              // Total Recovery (N'hood), Total Recovery (Site), Pushout Prevented (N'hood), Pushout Prevented (Site)
    { wch: 22 },                                                      // Lead Status
    { wch: 40 }                                                       // Notes
  ];

  const page2HeaderRow = 4;
  const page2NumCols = 27;
  for (let col = 0; col < page2NumCols; col++) {
    const cellRef = XLSX.utils.encode_cell({ r: page2HeaderRow, c: col });
    if (!page2Ws[cellRef]) page2Ws[cellRef] = { t: 's', v: '' };
    page2Ws[cellRef].s = headerStyle;
  }

  for (let row = page2HeaderRow + 1; row < page2Data.length; row++) {
    const isEven = (row - page2HeaderRow) % 2 === 0;
    const fillColor = isEven ? "E8E8E8" : "FFFFFF";
    for (let col = 0; col < page2NumCols; col++) {
      const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
      if (!page2Ws[cellRef]) page2Ws[cellRef] = { t: 's', v: '' };
      const isRecCol = col === 12 || col >= 15;
      page2Ws[cellRef].s = {
        fill: { fgColor: { rgb: fillColor } },
        alignment: { vertical: "top", horizontal: "left" as const, wrapText: isRecCol }
      };
    }
  }

  // Set maximum row height for all data rows
  page2Ws['!rows'] = [];
  for (let row = 0; row < page2Data.length; row++) {
    page2Ws['!rows'][row] = { hpt: row <= page2HeaderRow ? 20 : 60 };
  }

  // Add autofilter on header row so columns are sortable/filterable in Excel
  page2Ws['!autofilter'] = {
    ref: `${XLSX.utils.encode_cell({ r: page2HeaderRow, c: 0 })}:${XLSX.utils.encode_cell({ r: page2Data.length - 1, c: page2NumCols - 1 })}`
  };

  XLSX.utils.book_append_sheet(wb, page2Ws, 'Store Details');

  const safeName = brandName ? brandName.replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '_') : 'opportunities_by_region';
  const timestamp = new Date().toISOString().slice(0, 10);
  const filename = `${safeName}_${timestamp}.xlsx`;

  XLSX.writeFile(wb, filename);
}

/**
 * Generate and download a sample CSV template for import
 */
export function downloadSampleExcelTemplate(): void {
  const data = [
    ['brand_name', 'address', 'zip_code'],
    ['SAM\'S CLUB', '1421 N Renaissance Blvd NE Albuquerque New Mexico', '87107'],
    ['WALMART', '3500 Coors Blvd SW Albuquerque New Mexico', '87121'],
    ['WALMART', '8000 Academy Rd NE Albuquerque New Mexico', '87111']
  ];

  const ws = XLSX.utils.aoa_to_sheet(data);

  ws['!cols'] = [
    { wch: 15 },
    { wch: 45 },
    { wch: 12 }
  ];

  const headerRow = 0;
  for (let col = 0; col < 3; col++) {
    const cellRef = XLSX.utils.encode_cell({ r: headerRow, c: col });
    ws[cellRef].s = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "4472C4" } },
      alignment: { horizontal: "center", vertical: "center" }
    };
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Stores');
  XLSX.writeFile(wb, 'store_ids_template.xlsx');
}